import React, { FC, useState, useEffect } from 'react';
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import { Decision, Model, Price } from '../../types/model';
import Loading from '../../../../components/atoms/Loading';
import { useDispatch, useSelector } from 'react-redux';
import { ModelHistory } from '../../../../store/reducers/history';
import { getHistory } from '../../../../store/actions/ModelHistory';
import diff, {
  arrayDiff,
  ObjectDiff,
  ArrayDiff,
} from '../../../../utils/newDiff';
import ShowDiff, {
  ShowBasicDiff,
  ShowDecisionDiff,
  ShowPriceDiff,
} from './ShowDiff';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toSymbol } from '../../../../utils/formatting';
import { RootState } from '../../../../store/store';
import {
  DenyRule,
  isDecisionModel,
  isDenyModel,
  isPriceModel,
  Models,
} from '../../api/api-types';
import moment from 'moment';

interface HistoryModalProps {
  model: Models;
}

export type DataHistoryChanges = Record<string, any>;

interface DataHistoryObject {
  changes: DataHistoryChanges;
}

export interface DataHistoryArray {
  data: DataHistoryObject[];
}

// function reduceDeny(d: DenyRule[]) {
//     return d.reduce((c,n) => {
//         c[n.key] = `${n.key} ${n.symbol} ${n.value} as ${n.type}`;
//         return c;
//     }, {} as Record<string, string>);
// }
const denyFormat = (x: DenyRule) =>
  `${x.key} ${toSymbol(x.symbol)} '${x.value}' as ${x.type}`;
const History: FC<HistoryModalProps> = ({ model: apiModel }) => {
  const model = React.useMemo(
    () => ({
      //to get the rtk query model to work with the old model so we don't have to refactor
      ...apiModel,
      name: apiModel.name as string,
      // originFilters: apiModel.originFilters?.map((f) => f) ?? [],
      // stateFilters: apiModel.stateFilters?.map((f) => f) ?? [],
      // campaignFilters: apiModel.campaignFilters?.map((f) => f) ?? [],
      activeStart: moment(apiModel.activeStart),
      activeEnd: moment(apiModel.activeEnd),
    }),
    [apiModel],
  );

  const [loading, setLoading] = useState<boolean>(true);
  const history = useSelector<RootState, ModelHistory>(
    (x) =>
      x.history.byID[model.modelId] || {
        fetching: false,
        list: [],
        maxHistory: null,
      },
  );
  const dispatch = useDispatch();
  const [version, setVersion] = useState(1);
  const [changes, setChanges] = useState<ObjectDiff<Model> | null>(null);
  const [decision, setDecision] = useState<ObjectDiff<Decision> | null>(null);
  const [price, setPrice] = useState<ObjectDiff<Price> | null>(null);
  const [deny, setDeny] = useState<ArrayDiff<string[]> | null>(null);
  const [prev, setPrev] = useState<Model | null>(null);
  const [current, setCurrent] = useState<Model | null>(null);
  useEffect(() => {
    if (
      history.list.length <= version - 1 &&
      !history.fetching &&
      (!history.maxHistory || history.maxHistory < version)
    ) {
      setLoading(true);
      dispatch(getHistory(model.modelId, version));
    }
    // eslint-disable-next-line
  }, [version]);

  useEffect(() => {
    if (history.list.length >= version) {
      let prev = history.list[version - 1];
      let current = version > 1 ? history.list[version - 2] : model;
      //differenceJSON(history[version - 1], version > 1 ? history[version - 2] : model);
      setChanges(diff(current, prev));
      if (isDecisionModel(current) && isDecisionModel(prev)) {
        setDecision(
          current.decision && prev.decision
            ? diff(current.decision, prev.decision)
            : null,
        );
      }
      if (isDenyModel(current) && isDenyModel(prev)) {
        setDeny(
          current.denyRules && prev.denyRules
            ? arrayDiff(
              current.denyRules.map(denyFormat),
              prev.denyRules.map(denyFormat),
            )
            : null,
        );
      }
      if (isPriceModel(current) && isPriceModel(prev)) {
        setPrice(
          current.price && prev.price ? diff(current.price, prev.price) : null,
        );
      }

      setPrev(prev);
      setCurrent(current);
      setLoading(false);
    }
    if (history.maxHistory && version >= history.maxHistory) {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [version, history]);
  // TODO: Fix active start time in datebase for history. Right now history start/end is getting put in activeStart/activeEnd
  //`${x.key} ${x.symbol} ${x.value} as ${x.type}`
  return (
    <Stack>
      <Grid container>
        <Grid item>
          <IconButton
            disabled={version === 1}
            onClick={() => setVersion(version - 1)}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>

        <Grid item>
          {prev && (
            <Typography variant={'subtitle1'}>
              {prev.activeStart.format('MM/DD/YYYY h:mm A')} -{' '}
              {prev.activeEnd.format('MM/DD/YYYY h:mm A')}
            </Typography>
          )}
        </Grid>
        <Grid item>
          <IconButton
            style={{ position: 'relative' }}
            disabled={
              (history.maxHistory != null && version > history.maxHistory) ||
              loading
            }
            onClick={() => setVersion(version + 1)}>
            <ArrowForwardIcon />
            {loading && <Loading size={32} />}
          </IconButton>
        </Grid>
      </Grid>

      {changes && prev && <ShowDiff model={prev} diff={changes} />}
      {decision && <ShowDecisionDiff diff={decision} />}
      {deny && <ShowBasicDiff title="Deny" diff={deny} />}
      {price && <ShowPriceDiff diff={price} />}
    </Stack>
  );
};

export default History;
